import * as React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import { trackEvent } from 'integrations/metrics';
import { EventNames } from 'integrations/metrics/events';
import { EventAction, EventObjectType } from 'integrations/metrics/types';
import { getBookableAppointments } from 'selectors/bookableAppointments';
import { getSelectedServices } from 'selectors/services';
import validator from 'validator';
import * as yup from 'yup';

import Button from 'components/Button';
import { InputField } from 'components/InputField/InputField';
import Modal from 'components/Modal/Modal';
import SalonContact from 'components/SalonContact/SalonContact';
import { SmsConsent } from 'components/SmsConsent';

import { useBusiness } from 'hooks/business/useBusiness';
import { useCancellation } from 'hooks/cancellation/useCancellation';
import { useAppContext } from 'hooks/context/useAppContext';
import { usePreferences } from 'hooks/preferences/usePreferences';

import { CancellationPolicyAck } from 'routes/book/CancellationPolicyAck';

import { TAppointmentContact } from 'types/appointment';

import s from './Contact.module.css';

type TProps = {
  errorMessage?: string;
  defaultValues: TAppointmentContact;
  hideOfflineBookingModal: () => void;
  isLoading?: boolean;
  isOfflineBookingModalVisible: boolean;
  onSubmit: (values: TAppointmentContact) => Promise<any>;
};

export const Contact = ({
  errorMessage,
  defaultValues,
  hideOfflineBookingModal,
  isLoading,
  isOfflineBookingModalVisible,
  onSubmit,
}: TProps) => {
  const { publicUser } = useAppContext();
  const { isMultiUser, phone, email, cellPhone } = useBusiness();
  const { askForPronouns } = usePreferences();
  const bookableAppointments = useSelector(getBookableAppointments);
  const selectedServices = useSelector(getSelectedServices);

  const {
    cancellationPolicyFee,
    cancellationPolicyText,
    cancellationPolicyTimeWindow,
    cancellationPolicyForceAccept,
  } = useCancellation();

  const validationSchema = yup
    .object({
      firstName: yup.string().required('Please enter First Name'),
      lastName: yup.string().required('Please enter Last Name'),
      email: yup
        .string()
        .required('Please enter Email address')
        .test('is-email', 'Please enter a valid email address', (value) =>
          validator.isEmail(value),
        ),
      phone: yup
        .string()
        .required('Please enter Phone')
        .test('mobile-phone', 'Please enter a valid phone number', (value) =>
          validator.isMobilePhone(value, 'en-US'),
        ),
      cancellationPolicyAcknowledged: yup
        .bool()
        .oneOf(
          cancellationPolicyForceAccept ? [true] : [true, false],
          'Cancellation policy acknowledgment is required',
        ),
    })
    .required();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    control,
  } = useForm<TAppointmentContact>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  const submitForm: SubmitHandler<TAppointmentContact> = async (data) => {
    trackEvent(EventNames.NEXT_BUTTON_CLICKED_CLIENT_INFORMATION, {
      action: EventAction.SELECT,
      productSection: 'Booking',
      objectType: EventObjectType.BUTTON,
      bundle_size: bookableAppointments.length,
      is_msmp_booking: bookableAppointments.length > 1,
      service_ids: selectedServices,
    });

    await onSubmit(data);
  };

  return (
    <>
      <form
        className={s.root}
        onSubmit={(event) => void handleSubmit(submitForm)(event)}
        noValidate
      >
        <InputField
          id="first-name"
          label="First Name"
          name="firstName"
          register={register('firstName')}
          error={errors.firstName?.message}
          required
        />

        <InputField
          id="last-name"
          label="Last Name"
          name="lastName"
          register={register('lastName')}
          error={errors.lastName?.message}
          required
        />

        {askForPronouns ? (
          <InputField
            id="pronouns"
            label="Pronouns"
            name="pronouns"
            register={register('pronouns')}
            error={errors.pronouns?.message}
            required
          />
        ) : null}
        <InputField
          id="email"
          label="Email"
          type="email"
          name="email"
          register={register('email')}
          error={errors.email?.message}
          required
        />

        <InputField
          id="phone"
          label="Phone"
          type="tel"
          name="phone"
          control={control}
          error={errors.phone?.message}
          required
        />

        <SmsConsent
          className={s.sms}
          register={register('agreedToSmsConsent')}
        />
        {cancellationPolicyForceAccept && (
          <CancellationPolicyAck
            className={s.cancellationPolicy}
            fee={cancellationPolicyFee}
            policyText={cancellationPolicyText}
            timeWindow={cancellationPolicyTimeWindow}
            name="cancellationPolicyAcknowledged"
            register={register('cancellationPolicyAcknowledged')}
            error={errors.cancellationPolicyAcknowledged?.message}
          />
        )}

        {errorMessage && (
          <p className={s.appointmentErrorMessage}>
            {`Whoops! ${errorMessage}`}
          </p>
        )}
        <Button
          type="submit"
          className={s.button}
          loading={isSubmitting || isLoading}
          disabled={isSubmitting || isLoading}
        >
          Next
        </Button>
      </form>
      {isOfflineBookingModalVisible && (
        <Modal
          className={s.modal}
          onClose={hideOfflineBookingModal}
          title="Please Contact"
        >
          {publicUser.preferences.prompt_contact_on_booking_denial ? (
            <>
              <div>To schedule a booking please contact</div>
              <SalonContact
                businessEmail={email}
                businessPhone={phone}
                cellPhone={cellPhone}
              />
              <div>
                with your name,{' '}
                {isMultiUser && <>your provider&apos;s name, </>}
                service requested and the desired date/time of your booking.
              </div>
            </>
          ) : (
            <div>
              The professional you are requesting is not accepting online
              bookings at this time.
            </div>
          )}
        </Modal>
      )}
    </>
  );
};
